import {connect} from 'react-redux'
import {Dispatch} from 'redux'
import * as actions from '../actions/'
import {updateClientMetricMuted} from '../actions/'
import ManageAlerts, {Props} from '../components/ManageAlerts'
import {guidToString} from '../guids'
import {appPermissions} from '../roles-permissions'
import {AlertColumnName, AlertMessage, AlertTableSortSetting, AlertViewSettings, LimitType, Model, SortType,} from '../types/types'
import {alertLevelToComparable, assertNever, getNewLevel, getOneAlertPerClientAndMetric, limitTypeToComparable,} from '../utils'
import {ClientCode} from "../types/clients";

type ContainerStateProps = Pick<Props,
  | 'alerts'
  | 'clientCodesFromAlerts'
  | 'localTime'
  | 'mutedClientMetrics'
  | 'viewSettings'
  | 'hasPermission'
  | 'user'
  | 'clients'>

type ContainerDispatchProps = Pick<Props,
  | 'toggleMenuFor'
  | 'setColumnSort'
  | 'filterClientColumn'
  | 'filterDirectionColumn'
  | 'filterSeverityColumn'
  | 'filterMetricColumn'
  | 'clearFilters'
  | 'toggleMute'
  | 'toggleShowOnlyActiveAlerts'
  | 'showRedButtonFor'>

export function mapStateToProps({
                                  alerts,
                                  localTime,
                                  mutedClientMetrics,
                                  alertViewSettings,
                                  clients,
                                  user
                                }: Model): ContainerStateProps {
  return {
    alerts: applyViewSettings(alertViewSettings, alerts),
    clientCodesFromAlerts: getClientCodesFromAlert(alerts),
    clients,
    hasPermission: appPermissions,
    localTime,
    mutedClientMetrics,
    user,
    viewSettings: alertViewSettings,
  }
}

function getClientCodesFromAlert(
  alerts: ReadonlyArray<AlertMessage>,
): ReadonlySet<ClientCode> {
  return new Set(alerts.map(alert => alert.companyCode))
}

function excludeBaselines(alert: AlertMessage): boolean {
  return !(alert.alertSeverity === 'Notify' && alert.exceeded === false)
}

function applyViewSettings(
  settings: AlertViewSettings,
  alerts: ReadonlyArray<AlertMessage>,
): ReadonlyArray<AlertMessage> {
  // Is it worthwhile refactoring this to run in a single `.filter()` pass?

  // NB: This "active" filter is assuming the alerts list is sorted newest-first.
  const filteredByActive = settings.showOnlyActiveAlerts
    ? getOneAlertPerClientAndMetric(alerts).filter(excludeBaselines)
    : alerts

  const filteredByClientCode =
    settings.filterClient === undefined
      ? filteredByActive
      : filteredByActive.filter(
      alert => alert.companyCode === settings.filterClient,
      )

  // This only works because filterDirection has three states, one of which is
  // ruled out on the following line. Is there a cleaner way?
  const targetDirectionIsUpwards: boolean =
    settings.filterDirection === 'exceeding'
  const filteredByDirection =
    settings.filterDirection === 'all'
      ? filteredByClientCode
      : filteredByClientCode.filter(
      alert => alert.exceeded === targetDirectionIsUpwards,
      )

  const filteredBySeverity =
    settings.filterSeverityOrLevel[0] === 'all'
      ? filteredByDirection
      : settings.filterSeverityOrLevel[0] === 'severity'
        ? filteredByDirection.filter(
            (alert) =>
              alert.alertSeverity === settings.filterSeverityOrLevel[1],
          )
        : filteredByDirection.filter(
            (alert) =>
              getNewLevel(
                alert.alertSeverity,
                alert.exceeded,
                alert.limitType,
              ) === settings.filterSeverityOrLevel[1],
          )

  const filteredByMetric =
    settings.filterMetric === 'all'
      ? filteredBySeverity
      : filteredBySeverity.filter(
      alert => alert.limitType === settings.filterMetric,
      )

  return settings.sortBy.reduce(sortBySetting, filteredByMetric)
}

function sortBySetting(
  alerts: ReadonlyArray<AlertMessage>,
  setting: AlertTableSortSetting,
): ReadonlyArray<AlertMessage> {
  return [...alerts].sort((a, b) => columnToComparable(setting, a, b))
}

function columnToComparable(
  [key, direction]: AlertTableSortSetting,
  alertA: AlertMessage,
  alertB: AlertMessage,
): number {
  const flipper = direction === 'ascending' ? 1 : -1
  switch (key) {
    case 'rjeMessageNumber':
    case 'sequenceNumber':
      return flipper * (alertA[key] - alertB[key]);

    case 'percentValue':
    case 'thresholdPercent':
    case 'value':
      const a = alertA[key];
      const b = alertB[key]
      if (a == null) {
        return Number.MAX_SAFE_INTEGER;
      } else if (b == null) {
        return Number.MIN_SAFE_INTEGER;
      } else {
        return flipper * (a - b)
      }


    case 'rjeMessageTimestamp':
    case 'alertTimestamp':
      return flipper * (alertA[key].valueOf() - alertB[key].valueOf())

    case 'companyCode':
    case 'companyName':
    case 'virtualServerNumber':
      return flipper * alertA[key].localeCompare(alertB[key])

    case 'exceeded':
      return flipper * (alertA[key] === alertB[key] ? 0 : alertA[key] ? -1 : 1)

    case 'alertSeverity':
      return (
        flipper *
        (alertLevelToComparable(alertA[key]) -
          alertLevelToComparable(alertB[key]))
      )

    case 'clientId':
      return (
        flipper *
        guidToString(alertA[key]).localeCompare(guidToString(alertB[key]))
      )

    case 'limitType':
      return (
        flipper *
        (limitTypeToComparable(alertA[key]) -
          limitTypeToComparable(alertB[key]))
      )

    default:
      return assertNever(key)
  }
}

export function mapDispatchToProps(
  dispatch: Dispatch<actions.RootAction>,
): ContainerDispatchProps {
  return {
    filterClientColumn: (
      filter: AlertViewSettings['filterClient'],
    ): ReturnType<Props['filterClientColumn']> => (): void => {
      dispatch(actions.selectClient(filter))
      dispatch(actions.setAlertsClientFilter(filter))
    },
    filterDirectionColumn: (
      filter: AlertViewSettings['filterDirection'],
    ): ReturnType<Props['filterDirectionColumn']> => (): void => {
      dispatch(actions.setAlertsDirectionFilter(filter))
    },

    filterSeverityColumn: (
      filter: AlertViewSettings['filterSeverityOrLevel'],
    ): ReturnType<Props['filterSeverityColumn']> => (): void => {
      dispatch(actions.setAlertsSeverityFilter(filter))
    },

    filterMetricColumn: (
      filter: AlertViewSettings['filterMetric'],
    ): ReturnType<Props['filterMetricColumn']> => (): void => {
      dispatch(actions.setAlertsMetricFilter(filter))
    },

    clearFilters: (): void => {
      dispatch(actions.clearAlertsFilters())
    },

    setColumnSort: (
      field: keyof AlertMessage,
      sortType: SortType,
    ): ReturnType<Props['setColumnSort']> => (): void => {
      dispatch(actions.setAlertsColumnSort(field, sortType))
    },

    showRedButtonFor: (
      clientCode: ClientCode,
    ): ReturnType<Props['showRedButtonFor']> => (): void => {
      dispatch(actions.selectClient(clientCode))
    },
    toggleMenuFor: (
      column: AlertColumnName,
    ): ReturnType<Props['toggleMenuFor']> => (): void => {
      dispatch(actions.toggleAlertsColumnMenu(column))
    },

    toggleMute: (
      clientCode: ClientCode,
      metric: LimitType,
    ): ReturnType<Props['toggleMute']> => (): void => {
      dispatch(updateClientMetricMuted(clientCode, metric, 'toggle'))
    },
    toggleShowOnlyActiveAlerts: (): void => {
      dispatch(actions.toggleShowOnlyActiveAlerts('toggle'))
    },
  }
}

function mergeProps(
  stateProps: ContainerStateProps,
  dispatchProps: ContainerDispatchProps,
  ownProps: {}, // Is this the correct type???
): ContainerStateProps & ContainerDispatchProps {
  return {...ownProps, ...stateProps, ...dispatchProps}
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(ManageAlerts)
